import { miyagi } from "ldrs";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import {
  leagueSync,
  getLeagueSyncStatus,
} from "../../../Controllers/LeagueSyncApi";
import warningIcon from "../../../assets/imgs/delete_account.svg";
import { actions } from "../../../Redux/store";
import { hapticsImpactLight } from "../../../lib/utils";
import SyncStatusRenderer, { SyncStatus } from "./SyncStatusRenderer";
import { useMixpanel } from "../../../Context/MixpanelContext/MixpanelProvider";
import { useLeagueStore } from "../../../zustandStore/leagueStore";
import { useSocket } from "../../../Context/SocketContext/SocketProvider";
import { useLeagueInfo } from "../../../Context/LeagueInfoContext/LeagueInfoContext";

miyagi.register();
export default function SyncLeague() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLeagueSyncing, setIsLeagueSyncing] = useState(false);
  const [syncResponse, setSyncResponse] = useState<any>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncStatusResponse, setSyncStatusResponse] = useState<any>(null);
  const [syncStatusError, setSyncStatusError] = useState<string | null>(null);
  const [isLoadingSyncStatus, setIsLoadingSyncStatus] = useState(false);
  const { selectedLeague: storedLeague, setSelectedLeague: setStoredLeague } =
    useLeagueStore();
  const syncInitiated = useRef(false);
  const Mixpanel = useMixpanel();
  // Store navigation state data
  const leagueId = location.state?.leagueId;
  const provider = location.state?.provider;
  const sleeperData = location.state?.sleeperData;
  const espnData = location.state?.espnData;
  const { startNewThread } = useSocket();
  const { refetchLeagueInfo, filteredLeagueData } = useLeagueInfo();
  const syncLeagueData = async () => {
    if (!leagueId || !provider) return;

    setIsLeagueSyncing(true);
    setSyncError(null);

    try {
      let requestBody = {};

      if (provider === "SLEEPER" && sleeperData) {
        requestBody = {
          owner_id: sleeperData?.user_id,
        };
      }

      const response = await leagueSync(provider, leagueId, requestBody);

      if (response.status === 200 && response.data) {
        await checkLeagueSyncStatus(provider, leagueId);
        setSyncResponse(response.data);
      } else {
        console.log("Error syncing league:", String(response.errors));
        setSyncError("Failed to sync league");
      }
    } catch (error: any) {
      console.error("Error syncing league:", error);
      setSyncError("Failed to sync league");
      setSyncResponse(null);
    } finally {
      setIsLeagueSyncing(false);
    }
  };

  const checkLeagueSyncStatus = async (provider: string, leagueId: string) => {
    setIsLoadingSyncStatus(true);
    try {
      const statusResponse = await getLeagueSyncStatus(provider, leagueId);
      if (statusResponse.status === 200 && statusResponse.data) {
        setSyncStatusResponse(statusResponse.data);
        if (statusResponse.data?.status === "COMPLETED") {
          // Call getSlepperUserInfo when sync is completed
          console.log(sleeperData?.username, "sleeperData?.username");
          if (provider === "SLEEPER" && sleeperData?.user_id) {
            await fetchSleeperUserInfoAndFindLeague();
          }
        }
      } else {
        console.log(
          "Error getting league sync status:",
          String(statusResponse.errors),
        );
        setSyncStatusError("Failed to get sync status");
      }
    } catch (error: any) {
      console.error("Error getting league sync status:", error);
      setSyncStatusError("Failed to get sync status");
    } finally {
      setIsLoadingSyncStatus(false);
    }
  };

  const fetchSleeperUserInfoAndFindLeague = async () => {
    try {
      await refetchLeagueInfo();

      // Find the matching league object by comparing leagueId
      if (filteredLeagueData && Array.isArray(filteredLeagueData)) {
        const matchingLeague = filteredLeagueData.find(
          (league) => league.leagueId === leagueId,
        );

        if (matchingLeague) {
          setStoredLeague(matchingLeague);
          startNewThread(matchingLeague?.leagueId, matchingLeague?.ownerId);
        } else {
          console.log("No matching league found for leagueId:", leagueId);
          console.log("Available leagues:", filteredLeagueData);
        }
      } else {
        console.log(
          "filteredLeagueData is not available or not an array:",
          filteredLeagueData,
        );
      }
    } catch (error) {
      console.error("Error in fetchSleeperUserInfoAndFindLeague:", error);
    }
  };

  // Effect to handle initial sync
  useEffect(() => {
    console.log("League Sync Data:", {
      leagueId,
      provider,
      sleeperData,
      espnData,
    });

    // Prevent duplicate API calls by checking if sync has already been initiated
    if (syncInitiated.current) return;

    if (provider === "SLEEPER" || provider === "ESPN") {
      syncInitiated.current = true;
      // Using an immediately-invoked async function to await both API calls
      (async () => {
        await syncLeagueData();
      })();
    }
  }, [provider, sleeperData, espnData, leagueId, syncResponse]);

  const goBack = () => {
    hapticsImpactLight();
    navigate("/chatbot-ai");
    if (provider === "SLEEPER") {
      actions.modal.openLeagueModal(null);
      actions.modal.openSleeperModal(null);
    } else if (provider === "ESPN") {
      actions.modal.openLeagueModal(null);
    }
  };

  const getStarted = () => {
    Mixpanel.trackEvent("league_sync_complete_continue", {
      name: "User clicked continue after league sync",
    });
    hapticsImpactLight();
    navigate("/chatbot-ai");
  };

  return (
    <>
      {isLeagueSyncing || isLoadingSyncStatus ? (
        <div className="flex flex-col items-center justify-center h-screen gap-3">
          <l-grid size="80" speed="2.1" color="#00E0FF"></l-grid>
          <div className="text-center">
            <span className="text-[24px] text-white  font-medium text-center">
              Getting your league ready...
            </span>
          </div>
        </div>
      ) : syncError ? (
        <div className="w-full h-screen bg-[#000513]">
          <div className="flex flex-col h-full justify-center items-center">
            <img
              src={warningIcon}
              alt="warning"
              className="w-[50px] h-[50px]"
            />

            <div className="text-center">
              <div className="flex flex-col gap-1 px-3">
                <span className="text-white text-[24px] font-medium">
                  Unable to Sync League
                </span>
                <span className="text-[14px] text-[#FF0000] font-semibold mt-2">
                  {syncError}
                </span>
              </div>
            </div>
          </div>
          <div
            onClick={() => goBack()}
            className="fixed bottom-0 left-0 right-0 px-6"
            style={{
              paddingBottom: "calc(env(safe-area-inset-bottom) + 25px)",
            }}
          >
            <button className="bg-white w-full rounded-[50px] flex items-center justify-center h-[60px]">
              <span className="text-[16px] font-medium text-black">
                Try Again
              </span>
            </button>
          </div>
        </div>
      ) : (
        <SyncStatusRenderer
          status={syncStatusResponse?.status as SyncStatus}
          provider={provider}
          onGetStarted={getStarted}
          onTryAgain={() => checkLeagueSyncStatus(provider, leagueId)}
        />
      )}
    </>
  );
}
